import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { Search, AlertCircle, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBehaviorReportsForPicker } from "@/hooks/use-behavior-reports-for-picker";

const LOCATIONS = ["SALEM", "BOSTON", "METHUEN", "CAPE"] as const;

const LOCATION_COLORS: Record<string, string> = {
  SALEM: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  BOSTON: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  METHUEN: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  CAPE: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

interface InteractionReportQuickSwitcherProps {
  triggerVariant?: "button" | "inline";
  currentReportId?: string;
  currentLocation?: string;
  enableKeyboardShortcut?: boolean;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

export function InteractionReportQuickSwitcher({
  triggerVariant = "button",
  currentReportId,
  currentLocation,
  enableKeyboardShortcut = true,
  externalOpen,
  onExternalOpenChange,
}: InteractionReportQuickSwitcherProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onExternalOpenChange !== undefined ? onExternalOpenChange : setInternalOpen;
  const [searchText, setSearchText] = useState("");
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [, navigate] = useLocation();

  const { reports, isLoading, error } = useBehaviorReportsForPicker({
    locationFilter,
    searchText,
    limit: 10,
  });

  useEffect(() => {
    if (open && currentLocation) {
      setLocationFilter([currentLocation]);
    }
  }, [open, currentLocation]);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      if (!currentLocation) {
        setLocationFilter([]);
      }
    }
  }, [open, currentLocation]);

  // Keyboard shortcut: Ctrl+K (context-aware, controlled by parent)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enableKeyboardShortcut) return;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === "k") {
        const target = e.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();
        const isEditable =
          tagName === "input" ||
          tagName === "textarea" ||
          target.isContentEditable;

        if (!isEditable) {
          e.preventDefault();
          setOpen(true);
        }
      }
    },
    [enableKeyboardShortcut, setOpen]
  );

  useEffect(() => {
    if (!enableKeyboardShortcut) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, enableKeyboardShortcut]);

  const handleSelect = (reportId: string) => {
    setOpen(false);
    if (reportId !== currentReportId) {
      navigate(`/reports/behavior/${reportId}`);
    }
  };

  const selectLocation = (location: string) => {
    if (location === "ALL") {
      setLocationFilter([]);
    } else {
      setLocationFilter([location]);
    }
  };

  const isAllSelected = locationFilter.length === 0;
  const selectedLocation = locationFilter.length === 1 ? locationFilter[0] : null;

  const triggerButton =
    triggerVariant === "button" ? (
      <Button
        variant="outline"
        size="default"
        onClick={() => setOpen(true)}
        data-testid="button-interaction-report-switcher"
      >
        <Search className="h-4 w-4 mr-2" />
        Switch Report
      </Button>
    ) : (
      <Button
        variant="outline"
        size="default"
        onClick={() => setOpen(true)}
        data-testid="button-interaction-quick-jump"
      >
        <Search className="h-4 w-4 mr-2" />
        Quick Jump
        <kbd className="ml-2 pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}K
        </kbd>
      </Button>
    );

  return (
    <>
      {triggerButton}
      <CommandDialog open={open} onOpenChange={setOpen} data-testid="dialog-interaction-report-switcher">
        <CommandInput
          placeholder="Search interaction reports..."
          value={searchText}
          onValueChange={setSearchText}
          data-testid="input-interaction-report-search"
        />
        <div className="flex flex-wrap gap-1 px-3 py-2 border-b">
          <Badge
            variant={isAllSelected ? "default" : "outline"}
            className="cursor-pointer hover-elevate"
            onClick={() => selectLocation("ALL")}
            data-testid="filter-interaction-location-all"
          >
            All
          </Badge>
          {LOCATIONS.map((loc) => (
            <Badge
              key={loc}
              variant={selectedLocation === loc ? "default" : "outline"}
              className="cursor-pointer hover-elevate"
              onClick={() => selectLocation(loc)}
              data-testid={`filter-interaction-location-${loc.toLowerCase()}`}
            >
              {loc}
            </Badge>
          ))}
        </div>
        <CommandList>
          {error ? (
            <div
              className="flex flex-col items-center justify-center py-6 text-center text-sm text-destructive"
              data-testid="interaction-picker-error"
            >
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>Failed to load interaction reports</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => window.location.reload()}
              >
                Refresh page
              </Button>
            </div>
          ) : (
            <>
              <CommandEmpty data-testid="interaction-picker-empty">
                No interaction reports found{searchText ? ` matching "${searchText}"` : ""}.
              </CommandEmpty>
              <CommandGroup heading="Recent Interaction Reports">
                {isLoading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Loading reports...
                  </div>
                ) : (
                  reports.map((report) => {
                    const date = new Date(report.reportDate + "T00:00:00");
                    const formattedDate = format(date, "MMM d, yyyy");
                    const isCurrentReport = report.id === currentReportId;
                    const locCode = report.locationCode || "Unknown";

                    return (
                      <CommandItem
                        key={report.id}
                        value={`${locCode} ${formattedDate} ${report.reportDate}`}
                        onSelect={() => handleSelect(report.id)}
                        className="flex items-center justify-between gap-2"
                        data-testid={`interaction-picker-item-${report.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge
                            variant="secondary"
                            className={`shrink-0 ${LOCATION_COLORS[locCode] || ""}`}
                          >
                            {locCode}
                          </Badge>
                          <span className="truncate">{formattedDate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                          <MessageSquare className="h-3 w-3" />
                          {isCurrentReport && (
                            <Badge variant="outline" className="text-[10px] px-1">
                              Current
                            </Badge>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })
                )}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
